import React, { useState, useRef, useEffect } from 'react';

const DocumentAnnotator = ({ 
  imageUrl, 
  ocrData, 
  onAnnotationSave, 
  initialAnnotations = {} 
}) => {
  const [annotations, setAnnotations] = useState(initialAnnotations);
  const [selectedBox, setSelectedBox] = useState(null);
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Predefined label options
  const labelOptions = [
    'company_name',
    'invoice_no',
    'date',
    'amount',
    'tax_amount',
    'total_amount',
    'customer_name',
    'address',
    'gstin',
    'hsn_code'
  ];

  // Colors for different label types
  const labelColors = {
    company_name: '#FF6B6B',
    invoice_no: '#4ECDC4',
    date: '#45B7D1',
    amount: '#96CEB4',
    tax_amount: '#FFEAA7',
    total_amount: '#DDA0DD',
    customer_name: '#98D8C8',
    address: '#F7DC6F',
    gstin: '#BB8FCE',
    hsn_code: '#85C1E9'
  };

  useEffect(() => {
    if (imageRef.current && imageLoaded) {
      const img = imageRef.current;
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    }
  }, [imageLoaded]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const getScaledCoordinates = (box) => {
    if (!imageRef.current || !imageLoaded) return {};
    
    const img = imageRef.current;
    const scaleX = img.offsetWidth / imageDimensions.width;
    const scaleY = img.offsetHeight / imageDimensions.height;
    
    return {
      left: box.x * scaleX,
      top: box.y * scaleY,
      width: box.width * scaleX,
      height: box.height * scaleY
    };
  };

  const handleBoxClick = (predictionId) => {
    setSelectedBox(predictionId);
    setShowLabelInput(true);
  };

  const handleLabelAssign = (label) => {
    if (selectedBox && label) {
      const updatedAnnotations = {
        ...annotations,
        [selectedBox]: label
      };
      setAnnotations(updatedAnnotations);
      onAnnotationSave && onAnnotationSave(updatedAnnotations);
    }
    setShowLabelInput(false);
    setSelectedBox(null);
    setNewLabel('');
  };

  const handleCustomLabelSubmit = () => {
    if (newLabel.trim()) {
      handleLabelAssign(newLabel.trim());
    }
  };

  const removeAnnotation = (predictionId) => {
    const updatedAnnotations = { ...annotations };
    delete updatedAnnotations[predictionId];
    setAnnotations(updatedAnnotations);
    onAnnotationSave && onAnnotationSave(updatedAnnotations);
  };

  const getAnnotatedData = () => {
    const result = {};
    Object.entries(annotations).forEach(([predictionId, label]) => {
      const prediction = ocrData.predictions.find(p => p.detection_id === predictionId);
      if (prediction) {
        result[label] = prediction.class;
      }
    });
    return result;
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Document Annotation</h2>
          <p className="text-sm text-gray-600 mt-1">
            Click on detected text regions to assign labels
          </p>
        </div>
        
        <div className="p-4">
          {/* Image container with annotations */}
          <div 
            ref={containerRef}
            className="relative inline-block border border-gray-200 rounded-lg overflow-hidden"
            style={{ maxWidth: '100%' }}
          >
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Document to annotate"
              className="block max-w-full h-auto"
              onLoad={handleImageLoad}
            />
            
            {/* Overlay bounding boxes */}
            {imageLoaded && ocrData && ocrData.predictions && ocrData.predictions.map((prediction) => {
              const scaledCoords = getScaledCoordinates(prediction);
              const isSelected = selectedBox === prediction.detection_id;
              const label = annotations[prediction.detection_id];
              const color = label ? labelColors[label] || '#999' : '#007bff';
              
              return (
                <div
                  key={prediction.detection_id}
                  className={`absolute border-2 cursor-pointer transition-all duration-200 ${
                    isSelected ? 'border-4 border-yellow-400' : ''
                  }`}
                  style={{
                    left: scaledCoords.left,
                    top: scaledCoords.top,
                    width: scaledCoords.width,
                    height: scaledCoords.height,
                    borderColor: color,
                    backgroundColor: label ? `${color}20` : 'rgba(0, 123, 255, 0.1)'
                  }}
                  onClick={() => handleBoxClick(prediction.detection_id)}
                  title={`${prediction.class} (Confidence: ${(prediction.confidence * 100).toFixed(1)}%)`}
                >
                  {label && (
                    <div 
                      className="absolute -top-6 left-0 px-2 py-1 text-xs font-medium text-white rounded shadow-lg"
                      style={{ backgroundColor: color }}
                    >
                      {label}
                      <button
                        className="ml-2 text-white hover:text-red-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAnnotation(prediction.detection_id);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Label assignment modal */}
          {showLabelInput && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold mb-4">Assign Label</h3>
                
                {selectedBox && (
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium text-gray-700">Selected text:</p>
                    <p className="text-sm text-gray-900 mt-1">
                      {ocrData.predictions.find(p => p.detection_id === selectedBox)?.class}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Choose a label:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {labelOptions.map((option) => (
                        <button
                          key={option}
                          className="px-3 py-2 text-sm border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                          onClick={() => handleLabelAssign(option)}
                        >
                          {option.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Or create custom label:</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        placeholder="Enter custom label"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleCustomLabelSubmit();
                          }
                        }}
                      />
                      <button
                        onClick={handleCustomLabelSubmit}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowLabelInput(false);
                      setSelectedBox(null);
                      setNewLabel('');
                    }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results section */}
          {Object.keys(annotations).length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">Annotated Data</h3>
              <div className="space-y-2">
                {Object.entries(getAnnotatedData()).map(([label, text]) => (
                  <div key={label} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div>
                      <span className="font-medium text-gray-700">{label}:</span>
                      <span className="ml-2 text-gray-900">{text}</span>
                    </div>
                    <div 
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: labelColors[label] || '#999' }}
                    ></div>
                  </div>
                ))}
              </div>
              
              {/* Export button */}
              <button
                onClick={() => {
                  const data = getAnnotatedData();
                  console.log('Annotated data:', data);
                  navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                  alert('Annotated data copied to clipboard!');
                }}
                className="mt-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              >
                Copy JSON to Clipboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentAnnotator; 